/**
 * Phase 27.49.P3 — Tokenize a card DIRECTLY against Intuit's
 * `/quickbooks/v4/payments/tokens` endpoint from the browser.
 *
 * PCI: the PAN, CVV, and expiration NEVER touch the KR backend. This
 * hook is called from a component whose sensitive inputs live only in
 * `useRef`s. The caller passes the raw card fields, we POST directly to
 * Intuit, receive an opaque token (single-use, 15 min TTL), and pass
 * that on to the KR backend for the actual charge.
 */

export async function tokenizeCardWithIntuit({ tokenizationUrl, card }) {
  if (!tokenizationUrl) throw new Error('tokenization_url is required');
  if (!card?.number || !card?.expMonth || !card?.expYear || !card?.cvc) {
    throw new Error('Card number, expiration, and CVC are required');
  }

  const body = {
    card: {
      number: card.number.replace(/\s+/g, ''),
      expMonth: String(card.expMonth).padStart(2, '0'),
      expYear: String(card.expYear).length === 2 ? `20${card.expYear}` : String(card.expYear),
      cvc: card.cvc,
      name: card.name || '',
      address: card.address || undefined,
    },
  };

  let response;
  try {
    response = await fetch(tokenizationUrl, {
      method: 'POST',
      // No Authorization header — the tokens endpoint is public.
      // We deliberately do NOT send credentials so cookies/origin don't
      // leak into Intuit's log.
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network / CORS / DNS
    throw new Error(`Could not reach Intuit tokenization service: ${err?.message || err}`);
  }

  let payload;
  try { payload = await response.json(); }
  catch { throw new Error('Intuit returned an invalid response'); }

  if (!response.ok) {
    const detail = payload?.errors?.[0]?.detail
      || payload?.message
      || `Tokenization failed (HTTP ${response.status})`;
    throw new Error(detail);
  }
  if (!payload?.value) {
    throw new Error('Intuit did not return a token');
  }
  return {
    value: payload.value,                                  // opaque token string
    card: {
      last4: (card.number.replace(/\s+/g, '') || '').slice(-4),
      brand: payload?.card?.cardType || null,
    },
  };
}
